import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText, PlusCircle } from 'lucide-react'
import { getReports, calculateScoreAndGenerateDraft } from '../../lib/services/reports'
import { getAssignments, type Visit } from '../../lib/services/visits'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useIsMultiFacility } from '../../hooks/useTier'
import { protectedQueryKey } from '../../lib/queryClient'

function formatToken(value: string | null | undefined): string {
  if (!value) return 'Visit'
  return value.replace(/_/g, ' ')
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Date not set'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function visitLabel(visit: Visit): string {
  return `${visit.facility_name} - ${formatToken(visit.visit_type)} - ${formatDate(visit.visit_date)}`
}

function generationOptionLabel(visit: Visit, isMultiFacility: boolean): string {
  const reportType = `${formatToken(visit.visit_type)} report`
  if (isMultiFacility) return `${visit.facility_name} - ${reportType} - ${formatDate(visit.visit_date)}`
  return `${reportType} - ${formatDate(visit.visit_date)}`
}

export function ReportsListPage() {
  const { viewerContext } = useAuth()
  const isMultiFacility = useIsMultiFacility()
  const [visitIdInput, setVisitIdInput] = useState('')
  const [reportSearch, setReportSearch] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const identity = viewerContext
    ? { userId: viewerContext.user_id, tenantId: viewerContext.tenant_id }
    : null
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: protectedQueryKey(identity, 'reports'),
    queryFn: getReports,
    enabled: Boolean(identity),
  })
  const visitsQuery = useQuery({
    queryKey: protectedQueryKey(identity, 'reports', 'visits'),
    queryFn: getAssignments,
    enabled: Boolean(identity),
  })

  const reports = data ?? []
  const visits = visitsQuery.data ?? []
  const scopedFacilityIds = !isMultiFacility && viewerContext?.facility_ids?.length
    ? new Set(viewerContext.facility_ids)
    : null
  const visibleVisits = scopedFacilityIds
    ? visits.filter((visit) => visit.facility_id && scopedFacilityIds.has(visit.facility_id))
    : visits
  const visibleVisitIds = new Set(visibleVisits.map((visit) => visit.id))
  const visibleReports = scopedFacilityIds
    ? reports.filter((report) => visibleVisitIds.has(report.visit_id))
    : reports
  const visitById = new Map(visibleVisits.map((visit) => [visit.id, visit]))
  const draftableVisits = visibleVisits.filter((visit) => visit.status === 'scheduled' || visit.status === 'in_progress' || visit.status === 'in_review')

  function reportLabel(report: { visit_id: string; version: number }): string {
    const visit = visitById.get(report.visit_id)
    if (visit) return `${visit.facility_name} report v${report.version}`
    return `Report v${report.version}`
  }

  function reportVisitLabel(visitId: string): string {
    const visit = visitById.get(visitId)
    if (visit) return visitLabel(visit)
    return `Linked visit ending ${visitId.slice(-8)}`
  }

  const normalizedSearch = reportSearch.trim().toLowerCase()
  const filteredReports = normalizedSearch
    ? visibleReports.filter((report) => {
        const searchable = [
          reportLabel(report),
          reportVisitLabel(report.visit_id),
          report.status,
          formatDate(report.generated_at),
        ].join(' ').toLowerCase()
        return searchable.includes(normalizedSearch)
      })
    : visibleReports

  const reportGroups = filteredReports.reduce<Array<{ facilityName: string; reports: typeof reports }>>((groups, report) => {
    const facilityName = visitById.get(report.visit_id)?.facility_name ?? 'Unmatched visit'
    const existing = groups.find((group) => group.facilityName === facilityName)
    if (existing) {
      existing.reports.push(report)
    } else {
      groups.push({ facilityName, reports: [report] })
    }
    return groups
  }, [])

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Reports
        </p>
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Reports Center</h1>
        <p className="mt-1 text-sm text-slate-600">Draft and released reports with role-aware release and export controls.</p>
      </header>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Generate Draft</h2>
        </div>
        <p className="px-5 pt-5 text-sm text-slate-600">
          {isMultiFacility ? 'Choose an available facility visit to generate a draft report.' : 'Choose an available visit for your facility to generate a draft report.'}
        </p>
        <div className="flex flex-col gap-2 p-5 md:flex-row md:items-center">
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm md:w-72"
            value={visitIdInput}
            onChange={(e) => setVisitIdInput(e.target.value)}
            disabled={visitsQuery.isLoading || draftableVisits.length === 0}
          >
            <option value="">{visitsQuery.isLoading ? 'Loading visits...' : 'Select an available visit'}</option>
            {draftableVisits.map((visit) => (
              <option key={visit.id} value={visit.id}>
                {generationOptionLabel(visit, isMultiFacility)}
              </option>
            ))}
          </select>
          <button
            className="btn-primary text-sm"
            disabled={isCreating || !visitIdInput.trim()}
            onClick={async () => {
              setCreateError(null)
              setIsCreating(true)
              try {
                // #1799: calculate score first so reports:draft has a scores row
                await calculateScoreAndGenerateDraft(visitIdInput.trim())
                setVisitIdInput('')
                await refetch()
              } catch (err) {
                setCreateError((err as Error).message)
              } finally {
                setIsCreating(false)
              }
            }}
          >
            <PlusCircle size={16} className="mr-1.5 inline-block" />
            Create Draft
          </button>
        </div>
        {!visitsQuery.isLoading && draftableVisits.length === 0 && (
          <p className="px-5 pb-5 text-sm text-slate-500">No scheduled or active visits are available for draft report generation.</p>
        )}
        {visitsQuery.error && <p className="px-5 pb-5 text-sm text-red-600">{(visitsQuery.error as Error).message}</p>}
        {createError && <p className="px-5 pb-5 text-sm text-red-600">{createError}</p>}
      </section>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Reports</h2>
          <span className="text-xs font-semibold text-slate-500">{filteredReports.length} of {visibleReports.length} total</span>
        </div>
        <div className="space-y-4 p-5">
          <label className="block">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Search reports</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder={isMultiFacility ? 'Search by facility, visit type, status, or date' : 'Search by visit type, status, or date'}
              value={reportSearch}
              onChange={(event) => setReportSearch(event.target.value)}
            />
          </label>
          {isLoading && <p className="text-sm text-slate-500">Loading reports...</p>}
          {error && <p className="text-sm text-red-600">{(error as Error).message}</p>}

          {!isLoading && !error && visibleReports.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              {isMultiFacility ? 'No reports found for this tenant.' : 'No reports found for your facility.'}
            </div>
          )}

          {!isLoading && !error && visibleReports.length > 0 && filteredReports.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              No reports match the current search.
            </div>
          )}

          <div className="space-y-4">
            {reportGroups.map((group) => (
              <div key={group.facilityName} className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">{group.facilityName}</h3>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500">
                    {group.reports.length} report{group.reports.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="grid gap-3">
                  {group.reports.map((report) => (
                    <Link
                      key={report.report_id}
                      to={`/reports/${report.report_id}`}
                      className="v11-dashboard-card p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-[var(--brand-primary)]" />
                          <p className="text-sm font-bold text-slate-900">{reportLabel(report)}</p>
                        </div>
                        <span className={report.status === 'released' ? 'badge-released' : 'badge-in-progress'}>{report.status}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {reportVisitLabel(report.visit_id)} | Generated {formatDate(report.generated_at)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
